import * as XLSX from 'xlsx';
import { ChessPuzzle } from '../services/chessPuzzleService';
import { bulkImportPuzzles } from '../services/chessPuzzleService';
import { toast } from 'react-toastify';

/**
 * Excel-based Puzzle Import/Export System
 * Allows teachers to download a template, fill it with puzzles, and import back
 */

// Validation functions
const isValidFEN = (fen: string): boolean => {
    if (!fen || typeof fen !== 'string') return false;
    
    // Basic FEN validation: should have 6 parts separated by spaces
    const parts = fen.trim().split(/\s+/);
    if (parts.length < 4) return false;
    
    // First part should be the board position with 8 ranks separated by /
    const ranks = parts[0].split('/');
    if (ranks.length !== 8) return false;
    
    // Each rank should be valid (pieces and numbers)
    const validPieces = /^[pnbrqkPNBRQK1-8]+$/;
    for (const rank of ranks) {
        if (!validPieces.test(rank)) return false;
    }
    
    return true;
};

const isValidMoves = (moves: string): boolean => {
    if (!moves || typeof moves !== 'string') return false;
    
    // Moves should be space-separated algebraic notation
    const moveList = moves.trim().split(/\s+/);
    
    // Should have at least 1 move
    if (moveList.length === 0) return false;
    
    // Basic algebraic notation check (simplified)
    const validMove = /^[NBRQK]?[a-h]?[1-8]?x?[a-h][1-8](=[NBRQ])?[+#]?$/;
    const validCastle = /^O-O(-O)?[+#]?$/;
    
    for (const move of moveList) {
        if (!validMove.test(move) && !validCastle.test(move)) {
            return false;
        }
    }
    
    return true;
};

const isValidRating = (rating: any): boolean => {
    const num = Number(rating);
    return !isNaN(num) && num >= 800 && num <= 3000;
};

const isValidComplexity = (complexity: string): boolean => {
    return ['beginner', 'intermediate', 'advanced'].includes(complexity?.toLowerCase());
};

const isValidCategory = (category: string): boolean => {
    return ['tactics', 'endgame', 'opening', 'middlegame', 'strategy', 'calculation', 'pattern', 'checkmate', 'defense', 'castle'].includes(category?.toLowerCase());
};

/**
 * Generate and download Excel template (empty template with headers only)
 */
export const downloadPuzzleTemplate = () => {
    try {
        // Create workbook
        const wb = XLSX.utils.book_new();
        
        // Add instructions sheet
        const instructions = [
            ['Chess Puzzle Import Template'],
            [''],
            ['INSTRUCTIONS:'],
            ['1. Fill in the puzzle data in the "Puzzles" sheet'],
            ['2. Required fields: FEN, Moves, Rating, Complexity, Category'],
            ['3. Optional fields: Description, Hint, Themes'],
            ['4. Save the file and import it back into the system'],
            [''],
            ['FIELD DESCRIPTIONS:'],
            ['FEN: Board position in Forsyth-Edwards Notation'],
            ['Moves: Solution moves in algebraic notation (space-separated)'],
            ['Rating: Difficulty rating (800-3000)'],
            ['Complexity: beginner, intermediate, or advanced'],
            ['Category: tactics, endgame, opening, middlegame, strategy, calculation, pattern, checkmate, defense, castle'],
            ['Description: Brief description of the puzzle'],
            ['Hint: Helpful hint for solving'],
            ['Themes: Comma-separated themes (e.g., "fork, pin, sacrifice")'],
            [''],
            [''],
            ['VALIDATION RULES:'],
            ['- FEN must be valid chess position'],
            ['- Moves must be in algebraic notation'],
            ['- Rating must be between 800 and 3000'],
            ['- Complexity must be: beginner, intermediate, or advanced'],
            ['- Category must be one of the listed categories above'],
        ];
        
        const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);
        XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');
        
        // Add puzzles sheet with headers only (empty template)
        const headers = [['FEN', 'Moves', 'Rating', 'Complexity', 'Category', 'Description', 'Hint', 'Themes']];
        const wsPuzzles = XLSX.utils.aoa_to_sheet(headers);
        
        // Set column widths
        wsPuzzles['!cols'] = [
            { wch: 60 }, // FEN
            { wch: 30 }, // Moves
            { wch: 10 }, // Rating
            { wch: 15 }, // Complexity
            { wch: 15 }, // Category
            { wch: 50 }, // Description
            { wch: 40 }, // Hint
            { wch: 40 }  // Themes
        ];
        
        XLSX.utils.book_append_sheet(wb, wsPuzzles, 'Puzzles');
        
        // Generate Excel file
        const fileName = `chess_puzzles_template_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, fileName);
        
        toast.success('Template downloaded! Fill it with puzzles and import back.');
        return true;
    } catch (error) {
        console.error('Error generating template:', error);
        toast.error('Failed to generate template');
        return false;
    }
};

/**
 * Parse and validate Excel file
 */
export const parseExcelFile = async (file: File): Promise<Omit<ChessPuzzle, 'id' | 'createdAt' | 'createdBy'>[]> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const data = e.target?.result;
                const workbook = XLSX.read(data, { type: 'binary' });
                
                // Get the Puzzles sheet
                const sheetName = workbook.SheetNames.find(name => 
                    name.toLowerCase() === 'puzzles' || name.toLowerCase() === 'puzzle'
                ) || workbook.SheetNames[0];
                
                if (!sheetName) {
                    throw new Error('No puzzle sheet found in Excel file');
                }
                
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);
                
                if (jsonData.length === 0) {
                    throw new Error('No puzzles found in Excel file');
                }
                
                // Parse and validate puzzles
                const puzzles: Omit<ChessPuzzle, 'id' | 'createdAt' | 'createdBy'>[] = [];
                const errors: string[] = [];
                
                jsonData.forEach((row: any, index: number) => {
                    const rowNum = index + 2; // +2 because Excel is 1-indexed and has header row
                    
                    // Validate required fields
                    if (!row.FEN) {
                        errors.push(`Row ${rowNum}: Missing FEN`);
                        return;
                    }
                    if (!isValidFEN(row.FEN)) {
                        errors.push(`Row ${rowNum}: Invalid FEN format`);
                        return;
                    }
                    
                    if (!row.Moves) {
                        errors.push(`Row ${rowNum}: Missing Moves`);
                        return;
                    }
                    if (!isValidMoves(row.Moves)) {
                        errors.push(`Row ${rowNum}: Invalid moves format`);
                        return;
                    }
                    
                    if (!row.Rating) {
                        errors.push(`Row ${rowNum}: Missing Rating`);
                        return;
                    }
                    if (!isValidRating(row.Rating)) {
                        errors.push(`Row ${rowNum}: Rating must be between 800 and 3000`);
                        return;
                    }
                    
                    if (!row.Complexity) {
                        errors.push(`Row ${rowNum}: Missing Complexity`);
                        return;
                    }
                    if (!isValidComplexity(row.Complexity)) {
                        errors.push(`Row ${rowNum}: Complexity must be beginner, intermediate, or advanced`);
                        return;
                    }
                    
                    if (!row.Category) {
                        errors.push(`Row ${rowNum}: Missing Category`);
                        return;
                    }
                    if (!isValidCategory(row.Category)) {
                        errors.push(`Row ${rowNum}: Invalid category`);
                        return;
                    }
                    
                    // Parse themes
                    const themes = row.Themes 
                        ? row.Themes.split(',').map((t: string) => t.trim()).filter((t: string) => t)
                        : [];
                    
                    // Create puzzle object
                    puzzles.push({
                        fen: row.FEN.trim(),
                        moves: row.Moves.trim(),
                        rating: Number(row.Rating),
                        complexity: row.Complexity.toLowerCase() as 'beginner' | 'intermediate' | 'advanced',
                        category: row.Category.toLowerCase() as any,
                        description: row.Description?.trim() || `Puzzle from import (Rating: ${row.Rating})`,
                        hint: row.Hint?.trim() || 'Look for the best move sequence',
                        themes: themes.length > 0 ? themes : ['imported'],
                        source: 'custom'
                    });
                });
                
                if (errors.length > 0) {
                    const errorMessage = `Found ${errors.length} validation error(s):\n${errors.slice(0, 10).join('\n')}${errors.length > 10 ? '\n... and more' : ''}`;
                    reject(new Error(errorMessage));
                    return;
                }
                
                if (puzzles.length === 0) {
                    reject(new Error('No valid puzzles found in file'));
                    return;
                }
                
                resolve(puzzles);
            } catch (error) {
                reject(error);
            }
        };
        
        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };
        
        reader.readAsBinaryString(file);
    });
};

/**
 * Import puzzles from Excel file
 */
export const importPuzzlesFromExcel = async (
    file: File,
    serviceProviderId: string,
    userId: string,
    onProgress?: (current: number, total: number) => void
): Promise<{ success: boolean; count: number; errors?: string }> => {
    try {
        toast.info('📊 Parsing Excel file...');
        
        // Parse and validate file
        const puzzles = await parseExcelFile(file);
        
        // Check puzzle limit (200 puzzles max per import)
        const MAX_PUZZLES = 200;
        if (puzzles.length > MAX_PUZZLES) {
            const errorMessage = `Too many puzzles! Maximum ${MAX_PUZZLES} puzzles allowed per import. Your file contains ${puzzles.length} puzzles. Please split your file into smaller batches.`;
            toast.error(errorMessage);
            return {
                success: false,
                count: 0,
                errors: errorMessage
            };
        }
        
        toast.success(`✅ Validated ${puzzles.length} puzzles. Importing...`);
        
        // Import puzzles with progress tracking
        let importedCount = 0;
        const batchSize = 50;
        
        for (let i = 0; i < puzzles.length; i += batchSize) {
            const batch = puzzles.slice(i, i + batchSize);
            const count = await bulkImportPuzzles(serviceProviderId, batch, userId);
            importedCount += count;
            
            if (onProgress) {
                onProgress(Math.min(i + batchSize, puzzles.length), puzzles.length);
            }
        }
        
        if (importedCount > 0) {
            toast.success(`🎉 Successfully imported ${importedCount} puzzles!`);
        } else {
            toast.warning('⚠️ No new puzzles imported (all may already exist)');
        }
        
        return {
            success: importedCount > 0,
            count: importedCount
        };
    } catch (error) {
        console.error('Error importing puzzles from Excel:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        toast.error(`Failed to import: ${errorMessage}`);
        return {
            success: false,
            count: 0,
            errors: errorMessage
        };
    }
};

/**
 * Generate and download CSV template with example rows
 * Format: FEN,Moves,Rating,Complexity,Category,Description,Hint,Themes
 */
export const downloadCSVTemplate = () => {
    try {
        // CSV with header and example rows
        const csvLines = [
            // Header
            'FEN,Moves,Rating,Complexity,Category,Description,Hint,Themes',
            // Example row 1
            'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1,e2e4 e7e5 g1f3,1200,beginner,opening,Basic opening puzzle,Control the center,opening center',
            // Example row 2
            '5r1k/1R4b1/p2P2Pp/1p5n/2p5/P4B1P/1pP5/5R1K w - - 1 32,b7f7 h5g3 h1g2 g3f1,1500,intermediate,tactics,Fork and pin combination,Look for the fork,fork pin tactics',
            // Empty row for user to add their puzzles
            ',,,,,,,'
        ];
        
        const csvContent = csvLines.join('\n');
        
        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `chess_puzzles_template_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success('CSV template downloaded! Fill it with puzzles and import back.');
        return true;
    } catch (error) {
        console.error('Error generating CSV template:', error);
        toast.error('Failed to generate CSV template');
        return false;
    }
};

/**
 * Parse and validate CSV file with enhanced validation
 */
export const parseCSVFile = async (file: File): Promise<Omit<ChessPuzzle, 'id' | 'createdAt' | 'createdBy'>[]> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const csvContent = e.target?.result as string;
                const lines = csvContent.split('\n').filter(line => line.trim());
                
                if (lines.length < 2) {
                    throw new Error('CSV file must have at least a header row and one data row');
                }
                
                // Parse header
                const header = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
                const expectedHeaders = ['fen', 'moves', 'rating', 'complexity', 'category', 'description', 'hint', 'themes'];
                const headerLower = header.map(h => h.toLowerCase());
                
                // Check for required headers (case-insensitive)
                const hasRequiredHeaders = ['fen', 'moves', 'rating', 'complexity', 'category']
                    .every(req => headerLower.includes(req));
                
                if (!hasRequiredHeaders) {
                    throw new Error('CSV must contain columns: FEN, Moves, Rating, Complexity, Category');
                }
                
                // Parse CSV rows (handling quoted values)
                const parseCSVLine = (line: string): string[] => {
                    const result: string[] = [];
                    let current = '';
                    let inQuotes = false;
                    
                    for (let i = 0; i < line.length; i++) {
                        const char = line[i];
                        if (char === '"') {
                            if (inQuotes && line[i + 1] === '"') {
                                current += '"';
                                i++; // Skip next quote
                            } else {
                                inQuotes = !inQuotes;
                            }
                        } else if (char === ',' && !inQuotes) {
                            result.push(current.trim());
                            current = '';
                        } else {
                            current += char;
                        }
                    }
                    result.push(current.trim());
                    return result;
                };
                
                const puzzles: Omit<ChessPuzzle, 'id' | 'createdAt' | 'createdBy'>[] = [];
                const errors: string[] = [];
                
                // Process data rows
                for (let i = 1; i < lines.length; i++) {
                    const line = lines[i].trim();
                    if (!line) continue; // Skip empty lines
                    
                    const values = parseCSVLine(line);
                    const rowNum = i + 1;
                    
                    // Create row object from header mapping
                    const row: any = {};
                    header.forEach((h, idx) => {
                        row[h] = values[idx] || '';
                    });
                    
                    // Validate required fields (case-insensitive)
                    const fen = row.FEN || row.fen || '';
                    const moves = row.Moves || row.moves || '';
                    const rating = row.Rating || row.rating || '';
                    const complexity = row.Complexity || row.complexity || '';
                    const category = row.Category || row.category || '';
                    const description = row.Description || row.description || '';
                    const hint = row.Hint || row.hint || '';
                    const themes = row.Themes || row.themes || '';
                    
                    // Validation
                    if (!fen) {
                        errors.push(`Row ${rowNum}: Missing FEN`);
                        continue;
                    }
                    if (!isValidFEN(fen)) {
                        errors.push(`Row ${rowNum}: Invalid FEN format`);
                        continue;
                    }
                    
                    if (!moves) {
                        errors.push(`Row ${rowNum}: Missing Moves`);
                        continue;
                    }
                    if (!isValidMoves(moves)) {
                        errors.push(`Row ${rowNum}: Invalid moves format`);
                        continue;
                    }
                    
                    if (!rating) {
                        errors.push(`Row ${rowNum}: Missing Rating`);
                        continue;
                    }
                    const ratingNum = Number(rating);
                    if (!isValidRating(ratingNum)) {
                        errors.push(`Row ${rowNum}: Rating must be between 800 and 3000`);
                        continue;
                    }
                    
                    if (!complexity) {
                        errors.push(`Row ${rowNum}: Missing Complexity`);
                        continue;
                    }
                    if (!isValidComplexity(complexity)) {
                        errors.push(`Row ${rowNum}: Complexity must be beginner, intermediate, or advanced`);
                        continue;
                    }
                    
                    if (!category) {
                        errors.push(`Row ${rowNum}: Missing Category`);
                        continue;
                    }
                    if (!isValidCategory(category)) {
                        errors.push(`Row ${rowNum}: Invalid category`);
                        continue;
                    }
                    
                    // Parse themes
                    const themesArray = themes 
                        ? themes.split(',').map((t: string) => t.trim()).filter((t: string) => t)
                        : [];
                    
                    // Create puzzle object
                    puzzles.push({
                        fen: fen.trim(),
                        moves: moves.trim(),
                        rating: ratingNum,
                        complexity: complexity.toLowerCase() as 'beginner' | 'intermediate' | 'advanced',
                        category: category.toLowerCase() as any,
                        description: description.trim() || `Puzzle from import (Rating: ${ratingNum})`,
                        hint: hint.trim() || 'Look for the best move sequence',
                        themes: themesArray.length > 0 ? themesArray : ['imported'],
                        source: 'custom'
                    });
                }
                
                if (errors.length > 0) {
                    const errorMessage = `Found ${errors.length} validation error(s):\n${errors.slice(0, 10).join('\n')}${errors.length > 10 ? '\n... and more' : ''}`;
                    reject(new Error(errorMessage));
                    return;
                }
                
                if (puzzles.length === 0) {
                    reject(new Error('No valid puzzles found in CSV file'));
                    return;
                }
                
                resolve(puzzles);
            } catch (error) {
                reject(error);
            }
        };
        
        reader.onerror = () => {
            reject(new Error('Failed to read CSV file'));
        };
        
        reader.readAsText(file);
    });
};

/**
 * Import puzzles from CSV file with validation
 */
export const importPuzzlesFromCSVFile = async (
    file: File,
    serviceProviderId: string,
    userId: string,
    onProgress?: (current: number, total: number) => void
): Promise<{ success: boolean; count: number; errors?: string }> => {
    try {
        toast.info('📊 Parsing CSV file...');
        
        // Parse and validate file
        const puzzles = await parseCSVFile(file);
        
        // Check puzzle limit (200 puzzles max per import)
        const MAX_PUZZLES = 200;
        if (puzzles.length > MAX_PUZZLES) {
            const errorMessage = `Too many puzzles! Maximum ${MAX_PUZZLES} puzzles allowed per import. Your file contains ${puzzles.length} puzzles. Please split your file into smaller batches.`;
            toast.error(errorMessage);
            return {
                success: false,
                count: 0,
                errors: errorMessage
            };
        }
        
        toast.success(`✅ Validated ${puzzles.length} puzzles. Importing...`);
        
        // Import puzzles with progress tracking
        let importedCount = 0;
        const batchSize = 50;
        
        for (let i = 0; i < puzzles.length; i += batchSize) {
            const batch = puzzles.slice(i, i + batchSize);
            const count = await bulkImportPuzzles(serviceProviderId, batch, userId);
            importedCount += count;
            
            if (onProgress) {
                onProgress(Math.min(i + batchSize, puzzles.length), puzzles.length);
            }
        }
        
        if (importedCount > 0) {
            toast.success(`🎉 Successfully imported ${importedCount} puzzles!`);
        } else {
            toast.warning('⚠️ No new puzzles imported (all may already exist)');
        }
        
        return {
            success: importedCount > 0,
            count: importedCount
        };
    } catch (error) {
        console.error('Error importing puzzles from CSV:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        toast.error(`Failed to import: ${errorMessage}`);
        return {
            success: false,
            count: 0,
            errors: errorMessage
        };
    }
};
